// src/components/news/FeedNewsDiscussionCard.js

import React, {
  useMemo,
  useState,
} from 'react';

import {
  useNavigate,
} from 'react-router-dom';

import piNetworkLogo from '../../assets/pinetworklogo-icon.jpg';
import newsGoldTickIcon from '../../assets/newsgoldtick-icon.png';
import placeholderProfile from '../../assets/placeholder-profile.png';
import discussIcon from '../../assets/discuss-icon.png';

import './FeedNewsDiscussionCard.css';

function getTimeAgo(value) {
  if (!value) {
    return '';
  }

  const date = new Date(value);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return '';
  }

  const difference =
    Math.max(
      Date.now() -
        date.getTime(),
      0
    );

  const seconds =
    Math.floor(
      difference / 1000
    );

  if (seconds < 60) {
    return 'Just now';
  }

  const minutes =
    Math.floor(
      seconds / 60
    );

  if (minutes < 60) {
    return `${minutes}m ago`;
  }

  const hours =
    Math.floor(
      minutes / 60
    );

  if (hours < 24) {
    return `${hours}h ago`;
  }

  const days =
    Math.floor(
      hours / 24
    );

  if (days < 7) {
    return `${days}d ago`;
  }

  try {
    return new Intl.DateTimeFormat(
      undefined,
      {
        day: 'numeric',
        month: 'short',
      }
    ).format(date);
  } catch {
    return date.toLocaleDateString();
  }
}

function formatPublishedDate(value) {
  if (!value) {
    return '';
  }

  const date =
    new Date(value);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return '';
  }

  try {
    return new Intl.DateTimeFormat(
      undefined,
      {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      }
    ).format(date);
  } catch {
    return date.toLocaleDateString();
  }
}

function truncateText(
  value,
  maximumLength
) {
  const text =
    String(
      value || ''
    ).trim();

  if (
    !text ||
    text.length <=
      maximumLength
  ) {
    return text;
  }

  return `${text
    .slice(
      0,
      maximumLength
    )
    .trim()}…`;
}

function FeedNewsCommentPreview({
  comment,
}) {
  const author =
    comment?.author || {};

  const username =
    author.username || '';

  const authorName =
    author.name ||
    username ||
    'BarterFi User';

  const profilePic =
    author.profilePic ||
    placeholderProfile;

  const timestamp =
    getTimeAgo(
      comment?.createdAt
    );

  const content = (
    <>
      <img
        src={profilePic}
        alt=""
        className="feed-news-comment-avatar"
        onError={(event) => {
          event.currentTarget.src =
            placeholderProfile;
        }}
      />

      <div className="feed-news-comment-content">
        <div className="feed-news-comment-meta">
          <span className="feed-news-comment-name">
            {authorName}
          </span>

          {timestamp && (
            <>
              <span
                className="feed-news-comment-dot"
                aria-hidden="true"
              >
                •
              </span>

              <span className="feed-news-comment-time">
                {timestamp}
              </span>
            </>
          )}
        </div>

        <p className="feed-news-comment-text">
          {truncateText(
            comment?.text,
            120
          )}
        </p>
      </div>
    </>
  );



return (
  <div className="feed-news-comment">
    {content}
  </div>
);

}

function FeedNewsDiscussionCard({
  article,
  user,
  onOpen,
}) {
  const navigate =
    useNavigate();

  const [
    imageFailed,
    setImageFailed,
  ] = useState(false);

  const discussion =
    article?.discussion || {};

  const latestComments =
    useMemo(() => {
      if (
        !Array.isArray(
          discussion.latestComments
        )
      ) {
        return [];
      }

      return discussion.latestComments
        .filter(
          (comment) =>
            comment &&
            comment.text
        )
        .slice(0, 2);
    }, [
      discussion.latestComments,
    ]);

  if (!article?._id) {
    return null;
  }

  const title =
    article.title ||
    'Pi Network update';

  const excerpt =
    truncateText(
      article.excerpt,
      190
    );

  const publishedLabel =
    formatPublishedDate(
      article.publishedAt
    );

  const commentCount =
    Number(
      discussion.commentCount ||
      0
    );

  const participantCount =
    Number(
      discussion.participantCount ||
      0
    );

  const hasImage =
    Boolean(
      article.imageUrl
    ) &&
    !imageFailed;

  const viewerProfilePic =
    user?.profilePic ||
    placeholderProfile;

  const handleOpenDiscussion =
    () => {
      /*
       * Home provides onOpen so it can save the current
       * feed scroll position before navigating.
       */
      if (
        typeof onOpen ===
        'function'
      ) {
        onOpen(article);
        return;
      }

      /*
       * Fallback navigation keeps this component reusable
       * outside Home.
       */
      navigate(
        `/news/${article._id}`,
        {
          state: {
            article,
          },
        }
      );
    };

  const discussionLabel =
    participantCount > 0
      ? `${participantCount} ${
          participantCount === 1
            ? 'pioneer'
            : 'pioneers'
        } discussing`
      : commentCount > 0
      ? `${commentCount} ${
          commentCount === 1
            ? 'comment'
            : 'comments'
        }`
      : 'Be the first to discuss';

  return (
    <article
      className="feed-news-card"
      onClick={
        handleOpenDiscussion
      }
      role="button"
      tabIndex={0}
      aria-label={`Open discussion for ${title}`}
      onKeyDown={(event) => {
        if (
          event.key === 'Enter' ||
          event.key === ' '
        ) {
          event.preventDefault();

          handleOpenDiscussion();
        }
      }}
    >
      <div className="feed-news-card-header">
        <div className="feed-news-source">
          <img
            src={piNetworkLogo}
            alt=""
            className="feed-news-source-logo"
          />

          <div className="feed-news-source-copy">
            <div className="feed-news-source-name-row">
              <span className="feed-news-source-name">
                {article.sourceName ||
                  'Pi Network'}
              </span>

              {article.isVerifiedSource && (
                <img
                  src={
                    newsGoldTickIcon
                  }
                  alt="Verified official source"
                  className="feed-news-source-tick"
                />
              )}
            </div>

            <span className="feed-news-source-label">
              Official Pi discussion
              {publishedLabel
                ? ` • ${publishedLabel}`
                : ''}
            </span>
          </div>
        </div>
      </div>

      <div className="feed-news-card-media">
        {hasImage ? (
          <img
            src={article.imageUrl}
            alt=""
            className="feed-news-card-image"
            onError={() =>
              setImageFailed(true)
            }
          />
        ) : (
          <div className="feed-news-card-image-fallback">
            <img
              src={piNetworkLogo}
              alt=""
            />
          </div>
        )}
      </div>

      <div className="feed-news-card-body">
        <h3 className="feed-news-card-title">
          {title}
        </h3>

        {excerpt && (
          <p className="feed-news-card-excerpt">
            {excerpt}
          </p>
        )}

        <div className="feed-news-discussion-summary">
          <img
            src={discussIcon}
            alt=""
            aria-hidden="true"
            className="feed-news-discussion-icon"
          />

          <span>
            {discussionLabel}
          </span>

          {commentCount > 0 &&
            participantCount > 0 && (
              <span className="feed-news-total-comments">
                • {commentCount}{' '}
                {commentCount === 1
                  ? 'comment'
                  : 'comments'}
              </span>
            )}
        </div>

        {latestComments.length >
          0 && (
          <div className="feed-news-comments-preview">
            {latestComments.map(
              (comment) => (
                <FeedNewsCommentPreview
                  key={
                    comment.id ||
                    comment._id
                  }
                  comment={
                    comment
                  }
                />
              )
            )}
          </div>
        )}

        <div className="feed-news-comment-entry">
          <img
            src={viewerProfilePic}
            alt=""
            className="feed-news-comment-entry-avatar"
            onError={(event) => {
              event.currentTarget.src =
                placeholderProfile;
            }}
          />

          <div className="feed-news-comment-entry-shell">
            <span className="feed-news-comment-entry-placeholder">
              Share your thoughts...
            </span>
          </div>
        </div>
      </div>
    </article>
  );
}

export default FeedNewsDiscussionCard;
